import React, { useState, useRef, useEffect } from 'react';
import { getFieldDescription } from '../../constants/fieldDescriptions';
import { formatIntegrationPath } from '../../../../types/integrationPath';
import './FieldTooltip.css';

interface FieldTooltipProps {
  fieldKey: string;
}

export function FieldTooltip({ fieldKey }: FieldTooltipProps) {
  const description = getFieldDescription(fieldKey);
  const iconRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isHovering && iconRef.current && tooltipRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      // Position tooltip above the icon, centered horizontally
      let left = iconRect.left + iconRect.width / 2 - tooltipRect.width / 2;
      let top = iconRect.top - tooltipRect.height - 8;
      
      // Ensure tooltip doesn't go off screen horizontally
      if (left < 10) {
        left = 10;
      } else if (left + tooltipRect.width > window.innerWidth - 10) {
        left = window.innerWidth - tooltipRect.width - 10;
      }
      
      // If tooltip would go above viewport, show it below instead
      if (top < 10) {
        top = iconRect.bottom + 8;
      }
      
      setPosition({ top, left });
    }
  }, [isHovering]);

  // Don't render tooltip if field not found in descriptions
  if (!description) {
    return null;
  }

  return (
    <span 
      className="field-tooltip"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <span
        ref={iconRef}
        className="tooltip-icon"
        role="img"
        aria-label="Field help. Hover for details."
        title="Hover for field help"
      >
        ?
      </span>
      <span 
        ref={tooltipRef}
        className="tooltip-content"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        <div className="tooltip-description">{description.description}</div>

        <div className="tooltip-applies">
          <strong>Applies to:</strong>{' '}
          {description.applicableFor === 'both'
            ? 'Polling & configs'
            : description.applicableFor === 'polling'
              ? 'Polling documents'
              : 'Configs'}
        </div>
        
        {description.integrationPaths && description.integrationPaths.length > 0 && (
          <div className="tooltip-paths">
            <strong>Used by:</strong>{' '}
            {description.integrationPaths.map(formatIntegrationPath).join(', ')}
          </div>
        )}
        
        {description.defaultValue && (
          <div className="tooltip-default">
            <strong>Default:</strong> {description.defaultValue}
          </div>
        )}
      </span>
    </span>
  );
}
